import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { useSession } from '@/hooks/useSession';
import { useCourse } from '@/hooks/useCourse';
import usePermission from '@/hooks/usePermission';
import { CardAvatarStack } from '@/components/ui/card-avatar-stack';
import { Modal } from '@/components/custom/modal/modal';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { Filter, FilterActions, FilterContent, FilterInput } from '@/components/custom/filter';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { useModuleDetailsCache } from '../../Playful/useModuleDetailsCache';
import { CourseAboutPlayfulModuleCard } from './CourseAboutPlayfulModuleCard';

// Versão lúdica de CourseAbout (tela de módulos do curso, professor/admin).
// Sem progresso/matrícula (conceito de aluno); mantém gestão de conteúdo:
// publicar/despublicar módulo e o link para "Gerenciar conteúdo".
export function CourseAboutPlayful(props) {
  const { detailedCourse, loading, managers, students, uri } = props;
  const navigate = useNavigate();
  const currentUser = useSession()?.session?.user;
  const { getDetailedCourses } = useCourse();
  const { moduleDetails, loadingDetailsFor, fetchModuleDetails } = useModuleDetailsCache();

  const [searchTerm, setSearchTerm] = useState('');
  const [showAboutModal, setShowAboutModal] = useState(false);

  const modules = detailedCourse?.['learning-systems'];
  const filteredModules = modules?.filter(item => (searchTerm === '' ? true : item.title.toLowerCase().includes(searchTerm.toLowerCase())));

  const modulesAuthoredByUser = modules?.filter(item => item?.['creator_id'] === currentUser?.id || !item?.['authored_by_user']);
  const getSelectedModule = () => {
    if (modulesAuthoredByUser?.length <= 0 && currentUser?.profileNames.includes('Administrador')) {
      return modules?.[0]?.id;
    } else if (modulesAuthoredByUser?.length > 0) {
      return modulesAuthoredByUser?.[0]?.id;
    }
  };

  const canManageContent =
    usePermission('Gerenciar conteúdo', 'Cursos, módulos e aulas') &&
    (!detailedCourse?.['authored-by-user'] || (detailedCourse?.['authored-by-user'] && currentUser?.id === detailedCourse?.['creator-id']) || currentUser?.profileNames.includes('Administrador'));

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      { route: '/curso/admin', name: 'Cursos', isActive: false },
      { route: uri, name: detailedCourse?.title, 'official-content': detailedCourse?.['official-content'] ?? false, isActive: true }
    ]
  };

  if (!detailedCourse && loading) {
    return (
      <DashboardContent variant="plain">
        <Loader />
      </DashboardContent>
    );
  }

  const aboutIsLong = (detailedCourse?.about?.length ?? 0) > 300;

  return (
    <DashboardContent
      breadcrumbs={breadcrumbs}
      variant="plain"
    >
      <div className="relative mb-6 sm:mt-20">
        <TiaBanner
          className="h-full"
          image={JOINHA}
          imageAlt="Personagem do professor"
          imageHeight={200}
          imageOffset={190}
          bubbleCorner="tl"
          bubbleMaxWidth={600}
          text={`Aqui estão os módulos de "${detailedCourse?.title}"! 📚 Acompanhe o conteúdo, publique o que estiver pronto e dê uma olhada nas aulas.`}
        />
      </div>

      <section
        className="relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6 shadow-card md:p-8"
        style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) 150ms both` }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-20 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
        />

        <div className="relative flex flex-wrap items-center gap-5">
          <div className="min-w-[220px] flex-1">
            <h1
              className="mb-1 text-2xl font-extrabold leading-snug"
              style={{ color: '#222731' }}
            >
              {detailedCourse?.title}
            </h1>
          </div>

          <div className="flex shrink-0 flex-wrap gap-4">
            <div>
              <h4 className="mb-1 text-xs font-bold text-neutral-500">
                {t('courses.managers')} ({managers?.length ?? 0})
              </h4>
              {managers?.length > 0 && (
                <CardAvatarStack
                  data={managers}
                  modalTitle={t('courses.managers')}
                />
              )}
            </div>

            <div>
              <h4 className="mb-1 text-xs font-bold text-neutral-500">
                {t('projects.titleStudents')} ({students?.length ?? 0})
              </h4>
              {students?.length > 0 && (
                <CardAvatarStack
                  data={students}
                  modalTitle={t('courses.students')}
                />
              )}
            </div>
          </div>
        </div>

        {detailedCourse?.about && (
          <p className="relative mb-0 mt-4 text-base font-semibold text-neutral-500">
            {detailedCourse.about}
            {aboutIsLong && '...'}
            {aboutIsLong && (
              <button
                type="button"
                onClick={() => setShowAboutModal(true)}
                className="ml-1 font-bold text-[var(--color-mission)]"
              >
                Ver mais
              </button>
            )}
          </p>
        )}

        <Modal
          show={showAboutModal}
          onClose={() => setShowAboutModal(false)}
          contentClassName="modal__content--md"
        >
          <h3 className="modal__simple-title">Sobre o curso</h3>
          <div className="modal__body mb-0">
            <p className="mb-4">{detailedCourse?.about}</p>
          </div>
        </Modal>

        <div className="relative mt-6">
          <Filter>
            <FilterContent>
              <FilterInput
                label={t('textsCommon.research')}
                placeholder="Pesquisar por módulo"
                type="search"
                name="search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </FilterContent>

            {canManageContent && (
              <FilterActions>
                <PlayfulCardButton
                  variant="outline"
                  className="h-9 w-auto px-5"
                  onClick={() => navigate(`/curso/admin/gerenciar/${detailedCourse?.id}/${modules?.length > 0 ? getSelectedModule() : 'm'}/disciplinas-e-aulas/d`)}
                >
                  {t('button.manageContent')}
                </PlayfulCardButton>
              </FilterActions>
            )}
          </Filter>
        </div>

        {modules?.length === 0 && (
          <EmptyState
            type="add"
            title="Ainda não temos módulos aqui"
            text="que tal criá-los?"
            variant="plain"
          />
        )}

        {modules?.length > 0 && (
          <div className="relative mt-2 flex flex-col gap-3">
            {filteredModules?.map((module, i) => (
              <CourseAboutPlayfulModuleCard
                key={module.id}
                course={detailedCourse}
                module={module}
                index={i}
                details={moduleDetails[module.id]}
                isLoadingDetails={!!loadingDetailsFor[module.id]}
                onRequestDetails={() => fetchModuleDetails(module.id)}
                onDeleted={() => getDetailedCourses(detailedCourse.id)}
              />
            ))}
          </div>
        )}
      </section>
    </DashboardContent>
  );
}
